import {Actions, createEffect, ofType} from "@ngrx/effects";
import {inject} from "@angular/core";
import {VisitPlanHttpService} from "../../services/visit-plan-http.service";
import {CommonsFacadeService} from "../../../../commons/angular/state/commons/commons-facade.service";
import {
  catchError,
  concatMap,
  finalize,
  forkJoin,
  map,
  mergeMap,
  of,
  switchMap,
  withLatestFrom,
  Observable
} from "rxjs";
import {
  VisitPlanAccountActions,
  VisitPlanAccountAPiActions,
  VisitPlanAccountChannelActions
} from "./visit-plan-account.actions";
import {VisitPlanAccountModel} from "../../../models/visit-plan-account.model";
import {VisitPlanCellModel} from "../../../models/visit-plan-cell.model";
import {VisitPlanAccountCellChannelService} from "../../services/visit-plan-account-cell-channel.service";
import {Store, Action} from "@ngrx/store";
import {visitPlanAccountFeature} from "./visit-plan-account.state";

import {VisitPlanSlotTrackerModel} from "../../../models/visit-plan-slot-tracker.model";
import {VisitPlanModel} from "../../../models/visit-plan.model";
import {VisitPlanAccountAnalysisModel} from "../../../models/visit-plan-account-analysis.model";
import {VisitPlanScheduleFacadeService} from "../../services/visit-plan-schedule-facade.service";
import * as _ from "lodash";
import {tap} from "rxjs/operators";

export const getVisitPlanAccounts = createEffect(
  (actions$ = inject(Actions),
   visitPlanHttpService = inject(VisitPlanHttpService),
   visitPlanAccountsStore = inject(Store<VisitPlanAccountModel>),
   commonsFacadeService: CommonsFacadeService = inject(CommonsFacadeService)
  ) => {
    return actions$.pipe(
      ofType(VisitPlanAccountAPiActions.getVisitPlanAccounts),
      concatMap(payload =>
        of(payload).pipe(
          withLatestFrom(visitPlanAccountsStore.select(visitPlanAccountFeature.selectCursor()))
        )
      ),
      mergeMap(([payload, cursor]) => {
        commonsFacadeService.setLoaderOn();
        let paramsCopy = {...payload.params}
        if (!!cursor) {
          paramsCopy.cursor = cursor;
        }
        return visitPlanHttpService.getVisitPlanAccounts(payload.visitPlanId, paramsCopy).pipe(
          map((res: {
            list: VisitPlanAccountModel[]
            cursor: string
          }) => VisitPlanAccountAPiActions.getVisitPlanAccountsSuccess({
            visitPlanAccounts: res.list,
            visitPlanId: payload.visitPlanId,
            cursor: res.cursor
          })),
          catchError((error: {
            message: string
          }) => of(VisitPlanAccountAPiActions.getVisitPlanAccountsFailure({error}))),
          finalize(() => {
            commonsFacadeService.setLoaderOff()
          })
        )
      })
    );
  },
  {functional: true}
);

export const getVisitPlanAccountSuccess = createEffect(
  (actions$ = inject(Actions),
   visitPlanAccountCellChannelService = inject(VisitPlanAccountCellChannelService)) => {
    return actions$.pipe(
      ofType(VisitPlanAccountAPiActions.getVisitPlanAccountsSuccess),
      map((payload) => {
        payload.visitPlanAccounts.forEach(visitPlanAccount => {
          visitPlanAccount.visitSlots.forEach(cell => {
            visitPlanAccountCellChannelService.subscribeById(cell.id);
          })
        })
      })
    );
  },
  {functional: true, dispatch: false}
);

export const getAccountsSuccess = createEffect(
  (actions$ = inject(Actions),
   visitPlanHttpService = inject(VisitPlanHttpService),
   visitPlanAccountsStore = inject(Store<VisitPlanAccountModel>),
   commonsFacadeService: CommonsFacadeService = inject(CommonsFacadeService)
  ) => {
    return actions$.pipe(
      ofType(VisitPlanAccountAPiActions.getVisitPlanAccountsSuccess),
      mergeMap((payload) => {
        commonsFacadeService.setLoaderOn();
        // const paramsCopy = {}
        const paramsCopy = {
          accountIds: payload.visitPlanAccounts.map(a => a.accountId),
          limit: 30
        }
        return visitPlanHttpService.getVisitPlanAccountAnalysis(payload.visitPlanId, paramsCopy).pipe(
          map((res: {
            list: VisitPlanAccountAnalysisModel[]
            cursor: string
          }) => VisitPlanAccountAPiActions.getVisitPlanAccountsAnalysisSuccess({
            visitPlanAccountsAnalysis: res.list,
            cursor: res.cursor
          })),
          catchError((error: {
            message: string
          }) => of(VisitPlanAccountAPiActions.getVisitPlanAccountsAnalysisFailure({error}))),
          finalize(() => {
            commonsFacadeService.setLoaderOff()
          })
        )
      })
    );
  },
  {functional: true}
);

export const getAccountsAnalysis = createEffect(
  (actions$ = inject(Actions),
   visitPlanHttpService = inject(VisitPlanHttpService),
   visitPlanAccountsStore = inject(Store<VisitPlanAccountModel>),
   commonsFacadeService: CommonsFacadeService = inject(CommonsFacadeService)
  ) => {
    return actions$.pipe(
      ofType(VisitPlanAccountAPiActions.getVisitPlanAccountsAnalysis),
      mergeMap((payload) => {
        commonsFacadeService.setLoaderOn();
        const paramsCopy = {...payload.params};
        return visitPlanHttpService.getVisitPlanAccountAnalysis(payload.visitPlanId, paramsCopy).pipe(
          map((res: {
            list: VisitPlanAccountAnalysisModel[]
            cursor: string
          }) => VisitPlanAccountAPiActions.getVisitPlanAccountsAnalysisSuccess({
            visitPlanAccountsAnalysis: res.list,
            cursor: res.cursor
          })),
          catchError((error: {
            message: string
          }) => of(VisitPlanAccountAPiActions.getVisitPlanAccountsAnalysisFailure({error}))),
          finalize(() => {
            commonsFacadeService.setLoaderOff()
          })
        )
      })
    );
  },
  {functional: true}
);

export const deleteCallsFromCell = createEffect(
  (actions$ = inject(Actions),
   visitPlanHttpService = inject(VisitPlanHttpService),
   commonsFacadeService: CommonsFacadeService = inject(CommonsFacadeService)
  ) => {
    return actions$.pipe(
      ofType(VisitPlanAccountAPiActions.deleteCallsFromCell),
      mergeMap((payload) => {
        commonsFacadeService.setLoaderOn();
        return visitPlanHttpService.deleteCellCalls(payload.visitPlanId, payload.cellId, payload.callIds, payload.deleteRecurringConfiguration).pipe(
          map((res: VisitPlanCellModel) => VisitPlanAccountAPiActions.deleteCallsFromCellSuccess({visitPlanCell: res})),
          catchError((error: {
            message: string
          }) => of(VisitPlanAccountAPiActions.deleteCallsFromCellFailure({error}))),
          finalize(() => {
            commonsFacadeService.setLoaderOff()
          })
        )
      })
    );
  },
  {functional: true}
);

export const updateCellTracker = createEffect(
  (actions$ = inject(Actions),
   visitPlanHttpService = inject(VisitPlanHttpService),
   commonsFacadeService: CommonsFacadeService = inject(CommonsFacadeService)
  ) => {
    return actions$.pipe(
      ofType(VisitPlanAccountAPiActions.updateCellTracker),
      mergeMap((payload) => {
        commonsFacadeService.setLoaderOn();
        return visitPlanHttpService.updateCellTracker(payload.visitPlanId, payload.visitPlanCell, payload.callIdConfigurationUpdateOptions).pipe(
          map((res: VisitPlanSlotTrackerModel) => VisitPlanAccountAPiActions.updateCellTrackerSuccess({visitPlanCellTracker: res})),
          catchError((error: {
            message: string
          }) => of(VisitPlanAccountAPiActions.updateCellTrackerFailure({error}))),
          finalize(() => {
            commonsFacadeService.setLoaderOff()
          })
        )
      })
    );
  },
  {functional: true}
);

export const updateCallsTimeWindow = createEffect(
  (actions$ = inject(Actions),
   visitPlanHttpService = inject(VisitPlanHttpService),
   commonsFacadeService: CommonsFacadeService = inject(CommonsFacadeService)
  ) => {
    return actions$.pipe(
      ofType(VisitPlanAccountAPiActions.updateCallsTimeWindow),
      mergeMap((payload) => {
        commonsFacadeService.setLoaderOn();
        return visitPlanHttpService.updateCallsTimeWindow(payload.visitPlanId, payload.cellId, payload.list).pipe(
          map((visitPlanCells: VisitPlanCellModel[]) => VisitPlanAccountAPiActions.updateCallsTimeWindowSuccess({visitPlanCells: visitPlanCells})),
          catchError((error: {
            message: string
          }) => of(VisitPlanAccountAPiActions.updateCallsTimeWindowFailure({error}))),
          finalize(() => {
            commonsFacadeService.setLoaderOff()
          })
        )
      })
    );
  },
  {functional: true}
);

export const assignCallsToResource = createEffect(
  (actions$ = inject(Actions),
   visitPlanHttpService = inject(VisitPlanHttpService),
   visitPlanScheduleFacadeService = inject(VisitPlanScheduleFacadeService),
   commonsFacadeService: CommonsFacadeService = inject(CommonsFacadeService),
   store = inject(Store)
  ) => {
    return actions$.pipe(
      ofType(VisitPlanAccountAPiActions.assignCallsToResource),
      mergeMap((payload) => {
        commonsFacadeService.setLoaderOn();

        if (!payload.callIds?.length || !payload.accountId || !payload.dateRange) {
          commonsFacadeService.setLoaderOff();
          const error = {message: 'Missing assignment payload'};
          return of(VisitPlanAccountAPiActions.assignCallsToResourceFailure({
            error,
            cellId: payload.cellId,
            userMessage: getAssignCallsErrorMessage(error)
          }));
        }

        const assignment$: Observable<Action> = forkJoin({
          analysis: visitPlanHttpService.getVisitPlanAccountAnalysis(
            payload.visitPlanId,
            {accountIds: [payload.accountId], limit: 30}
          ),
          routes: visitPlanScheduleFacadeService.getVisitScheduleRoutesCursor(
            payload.visitPlanId,
            payload.visitScheduleId,
            {
              from: payload.dateRange.from,
              to: payload.dateRange.to,
              limit: 1000
            }
          ),
          jobs: visitPlanHttpService.getVisitPlanScheduleJobs(
            payload.visitPlanId,
            payload.visitScheduleId,
            {
              accountIds: [payload.accountId],
              from: payload.dateRange.from,
              to: payload.dateRange.to,
              limit: 1000
            }
          )
        }).pipe(
          withLatestFrom(store.select(visitPlanAccountFeature.selectVisitPlanAccounts())),
          switchMap(([{analysis, routes, jobs}, accounts]) => {
            const account = accounts.find(acc =>
              acc.visitSlots.some(slot => slot.id === payload.cellId)
            );
            const currentCell = account?.visitSlots.find(slot => slot.id === payload.cellId);

            if (!currentCell) {
              commonsFacadeService.setLoaderOff();
              const error = {message: 'Cell not found in store. Please refresh.'};
              return of(VisitPlanAccountAPiActions.assignCallsToResourceFailure({
                error,
                cellId: payload.cellId,
                userMessage: getAssignCallsErrorMessage(error)
              }));
            }

            const selectedCalls = currentCell.calls?.filter(call => payload.callIds.includes(call.id)) || [];

            if (!selectedCalls.length) {
              commonsFacadeService.setLoaderOff();
              const error = {message: 'Selected calls not found in cell.'};
              return of(VisitPlanAccountAPiActions.assignCallsToResourceFailure({
                error,
                cellId: payload.cellId,
                userMessage: getAssignCallsErrorMessage(error)
              }));
            }

            const accountAnalysis = analysis.list?.find(a => a.accountId === payload.accountId);
            const slotAnalysis = accountAnalysis?.slots?.find(slot => slot.slotId === payload.cellId);

            const callsData = selectedCalls
              .map(call => {
                const callAnalysis = slotAnalysis?.calls?.find(ca => ca.callId === call.id);
                return {
                  callId: call.id,
                  jobId: callAnalysis?.jobId || call.jobId,
                  routeId: callAnalysis?.routed ? callAnalysis.routeId : null
                };
              })
              .filter(cd => !!cd.jobId);

            const jobIds = callsData.map(cd => cd.jobId);
            const routedCalls = callsData.filter(cd => !!cd.routeId);

            if (!jobIds.length) {
              commonsFacadeService.setLoaderOff();
              const error = {message: 'Could not map calls to jobs.'};
              return of(VisitPlanAccountAPiActions.assignCallsToResourceFailure({
                error,
                cellId: payload.cellId,
                userMessage: getAssignCallsErrorMessage(error)
              }));
            }

            const routeResource = routes.list?.find(r => r.resource?.id === payload.resourceId);
            const jobsData = jobs.list || [];

            const stopsToAdd = _.chain(jobsData)
              .filter(j => _.includes(jobIds, j.id))
              .map('stops')
              .flatten()
              .value();

            const targetRouteId = routeResource?.id;
            const order = routeResource
              ? _.uniq(routeResource.stopsOrder.concat(_.map(stopsToAdd, 'id')))
              : _.map(stopsToAdd, 'id');

            let request$: Observable<unknown>;

            if (routeResource || routedCalls.length > 0) {
              if (routedCalls.length > 0 && targetRouteId) {
                const routeGroups = _.groupBy(routedCalls, 'routeId');
                const payloadArray = [];

                Object.keys(routeGroups).forEach(fromRouteId => {
                  if (fromRouteId !== targetRouteId) {
                    payloadArray.push({
                      id: fromRouteId,
                      remove: routeGroups[fromRouteId].map(rc => rc.jobId)
                    });
                  }
                });

                payloadArray.push({
                  id: targetRouteId,
                  add: jobIds,
                  order
                });

                request$ = visitPlanHttpService.editRoute(
                  payload.visitPlanId,
                  payload.visitScheduleId,
                  payloadArray,
                  {group: false}
                );
              } else if (routedCalls.length > 0 && !targetRouteId) {
                request$ = visitPlanScheduleFacadeService.createVisitScheduleRoute(
                  payload.visitPlanId,
                  payload.visitScheduleId,
                  {
                    resourceId: payload.resourceId,
                    jobIds
                  }
                );
              } else {
                const editPayload = getMovedTaskPayload(
                  jobIds,
                  null,
                  targetRouteId,
                  null,
                  order
                );

                request$ = visitPlanHttpService.editRoute(
                  payload.visitPlanId,
                  payload.visitScheduleId,
                  editPayload,
                  {group: false}
                );
              }
            } else {
              request$ = visitPlanScheduleFacadeService.createVisitScheduleRoute(
                payload.visitPlanId,
                payload.visitScheduleId,
                {
                  resourceId: payload.resourceId,
                  jobIds
                }
              );
            }

            return request$.pipe(
              map(() => {
                const assignedCallIdsSet = new Set(payload.callIds);
                const updatedCell: VisitPlanCellModel = {
                  ...currentCell,
                  calls: currentCell.calls.filter(call => !assignedCallIdsSet.has(call.id))
                };

                return VisitPlanAccountAPiActions.assignCallsToResourceSuccess({visitPlanCell: updatedCell});
              }),
              catchError((error: any) =>
                of(VisitPlanAccountAPiActions.assignCallsToResourceFailure({
                  error,
                  cellId: payload.cellId,
                  userMessage: getAssignCallsErrorMessage(error)
                }))
              )
            );
          }),
          catchError((error: any) =>
            of(VisitPlanAccountAPiActions.assignCallsToResourceFailure({
              error,
              cellId: payload.cellId,
              userMessage: getAssignCallsErrorMessage(error)
            }))
          ),
          finalize(() => {
            commonsFacadeService.setLoaderOff();
          })
        );

        return assignment$;
      })
    );
  },
  {functional: true}
);

export const discardCellTrackers = createEffect(
  (actions$ = inject(Actions),
   visitPlanHttpService = inject(VisitPlanHttpService),
   commonsFacadeService: CommonsFacadeService = inject(CommonsFacadeService)
  ) => {
    return actions$.pipe(
      ofType(VisitPlanAccountAPiActions.discardChanges),
      mergeMap((payload) => {
        commonsFacadeService.setLoaderOn();
        return visitPlanHttpService.discardCellTrackers(payload.visitPlanId, payload.includeIds, payload.excludeIds).pipe(
          map(() => VisitPlanAccountAPiActions.discardChangesSuccess({
            includeIds: payload.includeIds,
            excludeIds: payload.excludeIds
          })),
          catchError((error: {
            message: string
          }) => of(VisitPlanAccountAPiActions.discardChangesFailure({error}))),
          finalize(() => {
            commonsFacadeService.setLoaderOff()
          })
        )
      })
    );
  },
  {functional: true}
);

// export const discardChangesSuccessToast = createEffect(
//   (
//     actions$ = inject(Actions),
//     schedulingProjectAlertsService = inject(SchedulingProjectAlertsService)
//   ) => {
//     return actions$.pipe(
//       ofType(VisitPlanAccountAPiActions.discardChangesSuccess),
//       tap(() => {
//         schedulingProjectAlertsService.discardCellTrackersSuccess();
//       })
//     );
//   },
//   {functional: true, dispatch: false}
// );
//
// export const discardChangesFailureToast = createEffect(
//   (
//     actions$ = inject(Actions),
//     schedulingProjectAlertsService = inject(SchedulingProjectAlertsService)
//   ) => {
//     return actions$.pipe(
//       ofType(VisitPlanAccountAPiActions.discardChangesFailure),
//       tap(() => {
//         schedulingProjectAlertsService.discardCellTrackersError();
//       })
//     );
//   },
//   {functional: true, dispatch: false}
// );

export const createCall = createEffect(
  (actions$ = inject(Actions),
   visitPlanHttpService = inject(VisitPlanHttpService),
   commonsFacadeService: CommonsFacadeService = inject(CommonsFacadeService)
  ) => {
    return actions$.pipe(
      ofType(VisitPlanAccountAPiActions.createCall),
      mergeMap((payload) => {
        commonsFacadeService.setLoaderOn();
        return visitPlanHttpService.createCall(payload.visitPlanId, payload.cellId, payload.call, payload.recurrencePattern).pipe(
          map((visitPlanCell: VisitPlanCellModel) => VisitPlanAccountAPiActions.createCallSuccess({visitPlanCell: visitPlanCell})),
          catchError((error: {
            message: string
          }) => of(VisitPlanAccountAPiActions.createCallFailure({error}))),
          finalize(() => {
            commonsFacadeService.setLoaderOff()
          })
        )
      })
    );
  },
  {functional: true}
);


export const submitVisitPlanTracker = createEffect(
  (actions$ = inject(Actions),
   visitPlanHttpService = inject(VisitPlanHttpService),
   commonsFacadeService: CommonsFacadeService = inject(CommonsFacadeService)
  ) => {
    return actions$.pipe(
      ofType(VisitPlanAccountAPiActions.submitVisitPlanTracker),
      mergeMap((payload) => {
        commonsFacadeService.setLoaderOn();
        return visitPlanHttpService.submitVisitPlanTracker(payload.visitPlanId, payload.includeIds, payload.excludeIds).pipe(
          map((visitPlan: VisitPlanModel) => VisitPlanAccountAPiActions.submitVisitPlanTrackerSuccess({
            visitPlan,
            includeIds: payload.includeIds,
            excludeIds: payload.excludeIds
          })),
          catchError((error: {
            message: string
          }) => of(VisitPlanAccountAPiActions.submitVisitPlanTrackerFailure({error}))),
          finalize(() => {
            commonsFacadeService.setLoaderOff()
          })
        )
      })
    );
  },
  {functional: true}
);

export const subscribeAllVisitPlanAccountCellsChannel = createEffect(
  (actions$ = inject(Actions),
   visitPlanAccountCellChannelService = inject(VisitPlanAccountCellChannelService)) => {
    return actions$.pipe(
      ofType(VisitPlanAccountChannelActions.subscribe),
      mergeMap(payload =>
        visitPlanAccountCellChannelService.getChannelsValues().pipe(
          map((notification) => {
            return VisitPlanAccountChannelActions.notificationReceived({notification: notification})
          })
        )
      )
    );
  },
  {functional: true}
);

export const unsubscribeAllVisitPlanAccountCellChannels = createEffect(
  (actions$ = inject(Actions),
   visitPlanAccountCellChannelService = inject(VisitPlanAccountCellChannelService)) => {
    return actions$.pipe(
      ofType(VisitPlanAccountActions.removeAll, VisitPlanAccountActions.clearStore),
      map(payload => {
          visitPlanAccountCellChannelService.unSubscribeAll();
          return VisitPlanAccountChannelActions.unsubscribeSuccess();
        }
      )
    );
  },
  {functional: true}
);

const getMovedTaskPayload = (serviceIds, fromRouteId, toRouteId, fromOrderedIds, toOrderedIds) => {
  if (!toRouteId) {
    return [
      {
        id: fromRouteId,
        remove: serviceIds
      }
    ];
  } else if (fromRouteId === toRouteId) {
    return [
      {
        id: fromRouteId,
        order: fromOrderedIds
      }
    ];
  } else if (!fromRouteId && !!toRouteId) {
    return [
      {
        id: toRouteId,
        add: serviceIds,
        order: toOrderedIds
      }
    ];
  }

  return [
    {
      id: fromRouteId,
      remove: serviceIds
    },
    {
      id: toRouteId,
      add: serviceIds,
      order: toOrderedIds
    }
  ];
};

const getAssignCallsErrorMessage = (error: any): string => {
  const apiError = error?.error ?? error;
  const status = error?.status ?? apiError?.status;
  const moreInfo: string | undefined = apiError?.more_info;
  const lowerCaseInfo = moreInfo?.toLowerCase?.();
  const isSkillError =
    status === 412 &&
    (apiError?.code === 412205 ||
      (lowerCaseInfo && lowerCaseInfo.includes('missing required skills')));

  if (isSkillError) {
    return moreInfo || 'Selected resource is missing required skills for the chosen calls.';
  }

  return apiError?.message || 'Unable to assign calls. Please try again.';
};
export const syncVisitPlan = createEffect(
  (actions$ = inject(Actions),
   visitPlanHttpService = inject(VisitPlanHttpService),
   commonsFacadeService = inject(CommonsFacadeService)) => {
    return actions$.pipe(
      ofType(VisitPlanAccountAPiActions.syncVisitPlan),
      mergeMap((payload) => {
        commonsFacadeService.setLoaderOn()
        return visitPlanHttpService.syncVisitPlan(payload.visitPlanId, payload.accountIds)
          .pipe(
            map((visitPlan: VisitPlanModel) => VisitPlanAccountAPiActions.syncVisitPlanSuccess({visitPlan})),
          )
          .pipe(finalize(() => commonsFacadeService.setLoaderOff()))
      })
    )
  },
  {functional: true}
);
